import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useEnqueueSnackbar } from 'hooks';
import { useTestCreatives, useUpdateHypothesisSection } from '../../api';
import { useFilterOptions } from '../../api/getFilterOptions';
import useHypothesisGrouping from '../../hooks/useHypothesisGrouping';
import useCreativeFilters from '../../hooks/useCreativeFilters';
import useCreativeColumnVisibility from '../../hooks/useCreativeColumnVisibility';
import useTestCreativesViewState from '../../hooks/useTestCreativesViewState';
import type { HypothesisSection, UploadedFilter, } from '../../types/hypothesis';
import CreativesFilterBar from './CreativesFilterBar';
import TestCreativesContent from './TestCreativesContent';
import TestCreativesHeader from './TestCreativesHeader';
import LoadingOverlay from './LoadingOverlay';
import { ALL_COLUMN_DEFS } from './creativeColumns';
import useFiltersActive from '../../hooks/useFiltersActive';

const NO_HYPOTHESIS_KEY = 'No hypothesis';

interface TestCreativesListProps {
  defaultApp?: string;
}

const TestCreativesList: FC<TestCreativesListProps> = ({ defaultApp }) => {
  const { app, network, selectedCreatives, updateSelection } =
    useTestCreativesViewState({ defaultApp });

  const [groupByHypothesis, setGroupByHypothesis] = useState(true);
  const [uploadedFilter, setUploadedFilter] = useState<UploadedFilter>('last_week');

  const {
    filters,
    setHypothesis,
    setSearch,
    setFlow,
    setCustomer,
    setTechnology,
    setTpcFinishDate,
    setStatus,
  } = useCreativeFilters([]);

  const { data: displayCreatives = [], isLoading, isFetching } = useTestCreatives({
    params: {
      app,
      hypothesis: filters.hypothesis,
      customer: filters.customer ?? undefined,
      flow: filters.flow,
      technology: filters.technology,
      status: filters.status ?? undefined,
      search: filters.search || undefined,
      tpc_finish_date: filters.tpcFinishDate ?? undefined,
      uploaded: uploadedFilter,
    },
    config: { enabled: !!app },
  });

  const { data: filterOptionsData } = useFilterOptions({
    params: { app, network },
    config: { enabled: !!app },
  });

  const filterOptions = useMemo(
    () => ({
      hypotheses: filterOptionsData?.hypothesis ?? [],
      customers: filterOptionsData?.customer ?? [],
      flows: filterOptionsData?.flow ?? [],
      technologies: filterOptionsData?.technology ?? [],
    }),
    [filterOptionsData],
  );

  const {
    visibilityModel,
    columnOrder,
    toggleColumn,
    moveColumn,
    resetDefaults,
  } = useCreativeColumnVisibility();

  const visibleColumns = useMemo<GridColDef[]>(() => {
    return columnOrder
      .filter((key) => visibilityModel[key])
      .map((key) => ALL_COLUMN_DEFS[key])
      .filter(Boolean);
  }, [columnOrder, visibilityModel]);

  const { onShowErrorAlert } = useEnqueueSnackbar();

  const updateHypothesisSection = useUpdateHypothesisSection({
    config: {
      onError: (err) => {
        const message =
          (err.cause as { details?: string } | undefined)?.details ??
          'Failed to update hypothesis section';
        onShowErrorAlert(message, { delayAlert: 3500 });
      },
    },
  });

  const { thisWeek, upcoming, noHypothesis, hypothesisCount } =
    useHypothesisGrouping(displayCreatives);

  const allCreativeIds = useMemo(
    () => displayCreatives.map((c) => c.id),
    [displayCreatives],
  );

  const allSelected =
    allCreativeIds.length > 0 &&
    allCreativeIds.every((id) => selectedCreatives.includes(id));
  const someSelected =
    !allSelected && allCreativeIds.some((id) => selectedCreatives.includes(id));

  const handleToggleSelectAll = useCallback(() => {
    updateSelection(allSelected ? [] : allCreativeIds);
  }, [allSelected, allCreativeIds, updateSelection]);

  const handleSelectionChange = useCallback(
    (ids: string[]) => { updateSelection(ids); },
    [updateSelection],
  );

  const selectedCreativesRef = useRef(selectedCreatives);
  useEffect(() => {
    selectedCreativesRef.current = selectedCreatives;
  }, [selectedCreatives]);

  const idsByHypothesisRef = useRef(new Map<string, string[]>());
  useEffect(() => {
    const map = new Map<string, string[]>();
    for (const c of displayCreatives) {
      const key = c.hypothesis ?? NO_HYPOTHESIS_KEY;
      const existing = map.get(key);
      if (existing) existing.push(c.id);
      else map.set(key, [c.id]);
    }
    idsByHypothesisRef.current = map;
  }, [displayCreatives]);

  const handleGroupSelectionChange = useCallback(
    (hypothesis: string, ids: string[]) => {
      const groupIds = idsByHypothesisRef.current.get(hypothesis) ?? [];
      const groupSet = new Set(groupIds);
      const others = selectedCreativesRef.current.filter((id) => !groupSet.has(id));
      updateSelection([...others, ...ids]);
    },
    [updateSelection],
  );

  const displayCreativesRef = useRef(displayCreatives);
  useEffect(() => {
    displayCreativesRef.current = displayCreatives;
  }, [displayCreatives]);

  const handleSectionChange = useCallback(
    (hypothesis: string, section: HypothesisSection) => {
      const creatives = displayCreativesRef.current
        .filter((c) => c.hypothesis === hypothesis)
        .map((c) => ({
          creative_id: c.creative_id,
          creative_type: c.creative_type,
        }));
      if (creatives.length === 0) return;
      updateHypothesisSection.mutate({ creatives, test_period: section });
    },
    [updateHypothesisSection],
  );

  const sortedFlatCreatives = useMemo(
    () =>
      [...displayCreatives].sort((a, b) => {
        if (a.launch_status === 'launched' && b.launch_status !== 'launched') return -1;
        if (a.launch_status !== 'launched' && b.launch_status === 'launched') return 1;
        return new Date(b.uploaded).getTime() - new Date(a.uploaded).getTime();
      }),
    [displayCreatives],
  );

  const hasActiveFilters = useFiltersActive(filters, uploadedFilter);

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setHypothesis([]);
    setFlow([]);
    setCustomer(null);
    setTechnology([]);
    setTpcFinishDate(null);
    setStatus(null);
    setUploadedFilter('last_week');
  }, [
    setSearch,
    setHypothesis,
    setFlow,
    setCustomer,
    setTechnology,
    setTpcFinishDate,
    setStatus,
  ]);

  const isInitialLoading = isLoading;
  const isRefetching = !isLoading && isFetching;
  const hasResults = displayCreatives.length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, width: '100%' }}>
      <TestCreativesHeader
        totalCreatives={displayCreatives.length}
        hypothesisCount={hypothesisCount}
        groupByHypothesis={groupByHypothesis}
        onGroupByHypothesisChange={setGroupByHypothesis}
        allSelected={allSelected}
        someSelected={someSelected}
        onToggleSelectAll={handleToggleSelectAll}
        columnOrder={columnOrder}
        visibilityModel={visibilityModel}
        onToggleColumn={toggleColumn}
        onMoveColumn={moveColumn}
        onResetColumns={resetDefaults}
      />

      {/* Filter bar — stays mounted and interactive while the grid below refetches */}
      <Box sx={{ mb: 2.5 }}>
        <CreativesFilterBar
          search={filters.search}
          uploaded={uploadedFilter}
          hypothesis={filters.hypothesis}
          flow={filters.flow}
          customer={filters.customer}
          technology={filters.technology}
          tpcFinishDate={filters.tpcFinishDate}
          status={filters.status}
          filterOptions={filterOptions}
          onSearchChange={setSearch}
          onUploadedChange={setUploadedFilter}
          onHypothesisChange={setHypothesis}
          onFlowChange={setFlow}
          onCustomerChange={setCustomer}
          onTechnologyChange={setTechnology}
          onTpcFinishDateChange={setTpcFinishDate}
          onStatusChange={setStatus}
        />
      </Box>

      {/* Grid / groups — fallback shows ONLY here while filters refetch */}
      <LoadingOverlay
        isLoading={isRefetching}
        title="Loading test creatives..."
      >
        <TestCreativesContent
          isInitialLoading={isInitialLoading}
          hasResults={hasResults}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
          groupByHypothesis={groupByHypothesis}
          thisWeek={thisWeek}
          upcoming={upcoming}
          noHypothesis={noHypothesis}
          sortedFlatCreatives={sortedFlatCreatives}
          selectedCreatives={selectedCreatives}
          columns={visibleColumns}
          onGroupSelectionChange={handleGroupSelectionChange}
          onFlatSelectionChange={handleSelectionChange}
          onSectionChange={handleSectionChange}
        />
      </LoadingOverlay>
    </Paper>
  );
};

export default TestCreativesList;
